import React, { useMemo } from "react";
import {
  scaleBand,
  scaleLinear,
  scalePoint,
  line as d3Line,
  area as d3Area,
  arc as d3Arc,
  pie as d3Pie,
  max as d3Max,
  curveMonotoneX,
} from "d3";
import { num, yTicks, truncateLabel } from "./graphMath";
import { GRAPH_LABEL_CAP_ENABLED } from "../../../../constants/featureFlags";
import { deriveChartColor, isUsableColor } from "../../../../utils/chartColor";
import { autoTextColor } from "../../../../utils/contrast";

/**
 * GraphChart — pure, dependency-light SVG chart renderer for the Graph block.
 *
 * Built on d3 primitives (scale/shape/array) — the ONE charting library already
 * used most across the frontend (90 files vs recharts 2, chart.js 1); d3 is the
 * app's core geometry toolkit and emits plain inline SVG, matching the Builder
 * Blocks deck (06-graph-block-editor-unclipped: landscape viewBox 360x238, zero
 * baseline, gridlines, right-aligned y numerals, per-datapoint colors, white-halo
 * value labels) and the lightweight rule (SVG/CSS over raster). No 4th lib added.
 *
 * `data` is an array of { label, value, color } where color is already resolved
 * to a concrete CSS color by the caller.
 */

const VIEW_W = 360;
const VIEW_H = 238;
const AXIS = "#D0D5DD";
const GRID = "#EAECF0";
const INK = "#123051"; // brand ink
const LABEL = "#475467";
// gordon-0915-graph-block-area-no-fill-axis-labels-contrast: axis/category
// labels are flat dark fills with nothing behind them, so on an image-fill (or
// otherwise dark) Segment they read as invisible. Every chart type paints this
// same light card first, so label contrast never depends on the Segment's
// background — mirrors the "no data" empty state, which already did this.
const CARD_BG = "#F9FAFB";
const ChartCard = () => <rect x={0} y={0} width={VIEW_W} height={VIEW_H} fill={CARD_BG} rx={8} />;

// gordon-0915-graph-block-label-outline-subtle-and-outside-labels: shared
// halo so every label (value labels ON the chart AND axis/category labels
// OUTSIDE it) gets the same subtle 1px soft white outline instead of the
// value label's previous heavy 3px stroke (which read as a thick white
// blob around the number) or the axis/category labels' PREVIOUS outline —
// none at all, so they vanished against a busy/dark Segment background.
const LABEL_HALO_WIDTH = 1;
const labelHalo = { stroke: "#ffffff", strokeWidth: LABEL_HALO_WIDTH, paintOrder: "stroke" };

// EW-1063: SVG <text> has no wrap/truncate, so a long data-point label overflows
// the fixed viewBox. When the flag is ON, ellipsize each rendered label to a
// context char-budget; when OFF this is the identity (raw label -> byte-identical).
const AXIS_CHAR_PX = 5.5; // ~avg glyph advance of DM Sans at fontSize 10
const fitLabel = (label, budget) =>
  GRAPH_LABEL_CAP_ENABLED ? truncateLabel(label, budget) : label;
// Per-point horizontal budget for cartesian x-axis labels centred under each point.
const cartesianBudget = (plotW, n) =>
  Math.max(4, Math.floor(plotW / Math.max(n, 1) / AXIS_CHAR_PX));

// Subtle white-halo value label (paint-order: stroke) so gridlines never
// strike through, without the label reading as a thick white blob.
const ValueLabel = ({ x, y, children, anchor = "middle", fill = INK }) => (
  <text
    x={x}
    y={y}
    textAnchor={anchor}
    fontSize={11}
    fontWeight={600}
    fontFamily="'DM Sans', sans-serif"
    fill={fill}
    {...labelHalo}
    style={{ paintOrder: "stroke" }}
  >
    {children}
  </text>
);

// In-wedge percentage label (Pie/Donut only). Unlike ValueLabel above, this
// text sits directly on a variable, potentially dark, wedge fill — the
// white-stroke halo + fontWeight 600 combo ValueLabel uses for gridline
// clearance reads bold/blurry here instead, and doesn't contrast against a
// dark wedge. Auto-picks ink/white per wedge color and uses a soft 1px
// drop-shadow instead of a thick stroke halo.
const WedgeLabel = ({ x, y, children, wedgeColor, testId }) => (
  <text
    x={x}
    y={y}
    textAnchor="middle"
    fontSize={11}
    fontWeight={500}
    fontFamily="'DM Sans', sans-serif"
    fill={autoTextColor(wedgeColor, { fallback: "#ffffff" })}
    style={{ filter: "drop-shadow(0 1px 1px rgba(0,0,0,0.35))" }}
    data-testid={testId}
  >
    {children}
  </text>
);

// Same subtle halo as ValueLabel, applied to axis-tick and category labels
// OUTSIDE the chart body so they stay legible on any Segment background.
const AxisLabel = ({ x, y, children, anchor = "middle", fill = LABEL, testId }) => (
  <text
    x={x}
    y={y}
    textAnchor={anchor}
    fontSize={10}
    fontFamily="'DM Sans', sans-serif"
    fill={fill}
    data-testid={testId}
    {...labelHalo}
    style={{ paintOrder: "stroke" }}
  >
    {children}
  </text>
);

function CartesianFrame({ children, ticks, y, x0, x1, yBase }) {
  return (
    <>
      {ticks.map((t, i) => {
        const gy = y(t);
        return (
          <g key={`grid-${i}`}>
            <line x1={x0} x2={x1} y1={gy} y2={gy} stroke={t === 0 ? AXIS : GRID} strokeWidth={t === 0 ? 1.25 : 1} />
            <AxisLabel x={x0 - 6} y={gy + 3} anchor="end">
              {t}
            </AxisLabel>
          </g>
        );
      })}
      {children}
    </>
  );
}

const BarChart = ({ data, m, plotW, plotH }) => {
  const maxVal = d3Max(data, (d) => num(d.value)) || 1;
  const ticks = yTicks(maxVal);
  const x = scaleBand()
    .domain(data.map((d, i) => i))
    .range([m.left, m.left + plotW])
    .padding(0.28);
  const y = scaleLinear().domain([0, ticks[ticks.length - 1]]).range([m.top + plotH, m.top]);
  const y0 = y(0);
  return (
    <CartesianFrame ticks={ticks} y={y} x0={m.left} x1={m.left + plotW}>
      {data.map((d, i) => {
        const v = num(d.value);
        const bx = x(i);
        const by = y(Math.max(v, 0));
        const h = Math.abs(y0 - by);
        return (
          <g key={`bar-${i}`}>
            <rect x={bx} y={Math.min(by, y0)} width={x.bandwidth()} height={h} rx={3} fill={deriveChartColor(d.color, undefined, i)} />
            <ValueLabel x={bx + x.bandwidth() / 2} y={Math.min(by, y0) - 5}>
              {v}
            </ValueLabel>
            <AxisLabel x={bx + x.bandwidth() / 2} y={m.top + plotH + 15}>
              {fitLabel(d.label, cartesianBudget(plotW, data.length))}
            </AxisLabel>
          </g>
        );
      })}
    </CartesianFrame>
  );
};

const LineOrArea = ({ data, m, plotW, plotH, filled }) => {
  const maxVal = d3Max(data, (d) => num(d.value)) || 1;
  const ticks = yTicks(maxVal);
  const x = scalePoint()
    .domain(data.map((d, i) => i))
    .range([m.left + 8, m.left + plotW - 8]);
  const y = scaleLinear().domain([0, ticks[ticks.length - 1]]).range([m.top + plotH, m.top]);
  const stroke = deriveChartColor(data.find((d) => isUsableColor(d.color))?.color, undefined, 0);
  const lineGen = d3Line()
    .x((_, i) => x(i))
    .y((d) => y(num(d.value)))
    .curve(curveMonotoneX);
  const areaGen = d3Area()
    .x((_, i) => x(i))
    .y0(y(0))
    .y1((d) => y(num(d.value)))
    .curve(curveMonotoneX);
  return (
    <CartesianFrame ticks={ticks} y={y} x0={m.left} x1={m.left + plotW}>
      {/* gordon-0915: ~35% alpha so the fill is actually visible under the
          line, not the near-invisible 18% it was measured at. */}
      {filled && <path d={areaGen(data)} fill={stroke} fillOpacity={0.35} />}
      <path d={lineGen(data)} fill="none" stroke={stroke} strokeWidth={2.5} strokeLinejoin="round" strokeLinecap="round" />
      {data.map((d, i) => (
        <g key={`pt-${i}`}>
          <circle cx={x(i)} cy={y(num(d.value))} r={3.5} fill={isUsableColor(d.color) ? d.color : stroke} stroke="#fff" strokeWidth={1.5} />
          <ValueLabel x={x(i)} y={y(num(d.value)) - 8}>
            {num(d.value)}
          </ValueLabel>
          <AxisLabel x={x(i)} y={m.top + plotH + 15}>
            {fitLabel(d.label, cartesianBudget(plotW, data.length))}
          </AxisLabel>
        </g>
      ))}
    </CartesianFrame>
  );
};

const PieOrDonut = ({ data, donut }) => {
  const cx = VIEW_W / 2;
  const cy = VIEW_H / 2;
  const r = Math.min(VIEW_W, VIEW_H) / 2 - 24;
  const total = data.reduce((s, d) => s + Math.max(num(d.value), 0), 0) || 1;
  const arcs = d3Pie()
    .sort(null)
    .value((d) => Math.max(num(d.value), 0))(data);
  const arcGen = d3Arc()
    .innerRadius(donut ? r * 0.58 : 0)
    .outerRadius(r);
  const labelArc = d3Arc().innerRadius(r * 0.72).outerRadius(r * 0.72);
  // Resolved once per index so the wedge and its legend swatch always agree,
  // and so two never-colored datapoints get distinct shaded fallbacks rather
  // than both collapsing onto one flat swatch.
  const wedgeColor = (d, i) => deriveChartColor(d.color, undefined, i);
  return (
    <g transform={`translate(0,0)`}>
      {arcs.map((a, i) => {
        const d = data[i];
        const [lx, ly] = labelArc.centroid(a);
        const pct = Math.round((Math.max(num(d.value), 0) / total) * 100);
        const fill = wedgeColor(d, i);
        return (
          <g key={`arc-${i}`}>
            <path
              d={arcGen.startAngle(a.startAngle).endAngle(a.endAngle)({})}
              transform={`translate(${cx},${cy})`}
              fill={fill}
              stroke="#fff"
              strokeWidth={2}
              data-testid={`pie-wedge-${i}`}
            />
            {pct >= 6 && (
              <WedgeLabel x={cx + lx} y={cy + ly + 3} wedgeColor={fill} testId={`pie-label-${i}`}>
                {pct}%
              </WedgeLabel>
            )}
          </g>
        );
      })}
      {donut && (
        <text x={cx} y={cy + 4} textAnchor="middle" fontSize={13} fontWeight={700} fontFamily="'DM Sans', sans-serif" fill={INK}>
          {total}
        </text>
      )}
      {/* legend — a translucent chip guarantees AA contrast for the ink text
          regardless of what's behind the chart (image-fill segment, dark
          theme, etc.) without needing to sample the card's own background. */}
      {data.map((d, i) => {
        const cols = 2;
        const col = i % cols;
        const row = Math.floor(i / cols);
        const lx = 14 + col * (VIEW_W / cols);
        const ly = VIEW_H - 22 + row * 14;
        const labelText = fitLabel(d.label, Math.floor((VIEW_W / 2 - 18) / AXIS_CHAR_PX));
        const chipWidth = 18 + labelText.length * AXIS_CHAR_PX + 6;
        return (
          <g key={`lg-${i}`} transform={`translate(${lx},${ly})`}>
            <rect
              x={-4}
              y={-13}
              width={chipWidth}
              height={18}
              rx={9}
              fill="rgba(255,255,255,0.86)"
              data-testid={`pie-legend-chip-${i}`}
            />
            <rect x={0} y={-8} width={9} height={9} rx={2} fill={wedgeColor(d, i)} />
            <AxisLabel x={14} y={0} anchor="start" fill={INK} testId={`pie-legend-label-${i}`}>
              {labelText}
            </AxisLabel>
          </g>
        );
      })}
    </g>
  );
};

const Radar = ({ data }) => {
  const cx = VIEW_W / 2;
  const cy = VIEW_H / 2 - 4;
  const r = Math.min(VIEW_W, VIEW_H) / 2 - 30;
  const n = Math.max(data.length, 3);
  const maxVal = d3Max(data, (d) => num(d.value)) || 1;
  const rings = [0.25, 0.5, 0.75, 1];
  const angle = (i) => (Math.PI * 2 * i) / n - Math.PI / 2;
  const point = (i, ratio) => [cx + Math.cos(angle(i)) * r * ratio, cy + Math.sin(angle(i)) * r * ratio];
  const stroke = deriveChartColor(data.find((d) => isUsableColor(d.color))?.color, undefined, 0);
  const poly = data.map((d, i) => point(i, Math.max(num(d.value), 0) / maxVal).join(",")).join(" ");
  return (
    <g>
      {rings.map((rr, ri) => (
        <polygon
          key={`ring-${ri}`}
          points={data.map((_, i) => point(i, rr).join(",")).join(" ")}
          fill="none"
          stroke={GRID}
          strokeWidth={1}
        />
      ))}
      {data.map((_, i) => {
        const [px, py] = point(i, 1);
        return <line key={`spoke-${i}`} x1={cx} y1={cy} x2={px} y2={py} stroke={GRID} strokeWidth={1} />;
      })}
      <polygon points={poly} fill={stroke} fillOpacity={0.2} stroke={stroke} strokeWidth={2} strokeLinejoin="round" />
      {data.map((d, i) => {
        const [px, py] = point(i, Math.max(num(d.value), 0) / maxVal);
        const [lx, ly] = point(i, 1.14);
        return (
          <g key={`rp-${i}`}>
            <circle cx={px} cy={py} r={3} fill={isUsableColor(d.color) ? d.color : stroke} stroke="#fff" strokeWidth={1.25} />
            <AxisLabel x={lx} y={ly + 3}>
              {fitLabel(d.label, 14)}
            </AxisLabel>
          </g>
        );
      })}
    </g>
  );
};

// gordon-0915-graph-block-remove-scatter-chart-type: Scatter is no longer an
// offered chart type, but hundreds of saved blocks still have chartType:
// "scatter" stored — render those as Line (a scatter plot here was always
// just a line chart with the line hidden). The stored prop is never mutated;
// this mapping only affects what gets rendered.
const normalizeChartType = (chartType) => (chartType === "scatter" ? "line" : chartType);

const GraphChart = ({ chartType = "bar", data = [] }) => {
  const effectiveChartType = normalizeChartType(chartType);
  const clean = useMemo(
    () => (Array.isArray(data) ? data.filter((d) => d && (d.label !== undefined || d.value !== undefined)) : []),
    [data]
  );

  if (!clean.length) {
    return (
      <svg viewBox={`0 0 ${VIEW_W} ${VIEW_H}`} width="100%" role="img" aria-label="Empty chart" style={{ maxWidth: "none" }}>
        <ChartCard />
        <text x={VIEW_W / 2} y={VIEW_H / 2} textAnchor="middle" fontSize={12} fontFamily="'DM Sans', sans-serif" fill={LABEL}>
          Add data points to see your chart
        </text>
      </svg>
    );
  }

  const m = { top: 16, right: 14, bottom: 26, left: 34 };
  const plotW = VIEW_W - m.left - m.right;
  const plotH = VIEW_H - m.top - m.bottom;

  let body;
  switch (effectiveChartType) {
    case "line":
      body = <LineOrArea data={clean} m={m} plotW={plotW} plotH={plotH} filled={false} />;
      break;
    case "area":
      body = <LineOrArea data={clean} m={m} plotW={plotW} plotH={plotH} filled />;
      break;
    case "pie":
      body = <PieOrDonut data={clean} donut={false} />;
      break;
    case "donut":
      body = <PieOrDonut data={clean} donut />;
      break;
    case "radar":
      body = <Radar data={clean} />;
      break;
    case "bar":
    default:
      body = <BarChart data={clean} m={m} plotW={plotW} plotH={plotH} />;
      break;
  }

  return (
    <svg
      viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
      width="100%"
      role="img"
      aria-label={`${effectiveChartType} chart`}
      // Opt out of the app's global svg{max-width:290px} clamp (deck N10 fix).
      style={{ maxWidth: "none", overflow: "visible" }}
    >
      <ChartCard />
      {body}
    </svg>
  );
};

export default GraphChart;
